// src/utils/generateProtokoll.js
// Sitzungsprotokoll als Excel (exceljs) aufbauen und als Download bereitstellen,
// plus eine vorbefüllte mailto-Vorbereitung (kein Datei-Anhang per mailto möglich,
// daher: Datei wird heruntergeladen, E-Mail-Text bittet um manuellen Anhang –
// TECHNISCHER KONTEXT von US-SITZ-002 sieht diesen Fallback ausdrücklich vor,
// da im Projekt keine bestehende Mailto-/Versandlösung mit Anhang existiert).
// US-SITZ-002: Protokollgenerierung (intern/extern), Kopf mit Anwesenden,
// Pendenzen der Sitzung + Übertrag offener Pendenzen, E-Mail-Vorbereitung.
//
// SICHERHEITSHINWEIS: Die Trennung intern/extern wird HIER beim Datenaufbau
// erzwungen (filterForMode), nicht nur optisch in der UI ausgeblendet. Im
// Modus "extern" verlässt kein Eintrag mit sichtbarExtern !== true diese Datei.
import ExcelJS from "exceljs";
import { richTextToPlainText } from "./richText";

const ART_LABELS = { P: "Pendenz", E: "Entscheid", N: "Notiz" };
const STATUS_LABELS = {
  offen: "Offen",
  inBearbeitung: "In Bearbeitung",
  kritisch: "Kritisch",
  erledigt: "Erledigt",
};

export const formatDateDE = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const todayIso = () => {
  const d = new Date();
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d - tzOffset).toISOString().slice(0, 10);
};

export const sanitizeFilenamePart = (s) => (s || "").toString().trim().replace(/[^\w-]+/g, "_");

// Exportiert, damit z.B. generateSitzungPdf.js (US-SITZ-005) dieselbe
// Anwesenden-Formatierung nutzt statt sie zu duplizieren.
export const attendeeDisplay = (a) => `${a.label}${a.funktion ? ` (${a.funktion})` : ""}`;

// Löst die E-Mail-Adresse einer Anwesenden-Option auf. "mf:general" hat keine
// eigene Adresse (Malheur&Fortuna allgemein) und wird übersprungen.
export const resolveAttendeeEmail = (optionId, { partners = [], persons = [], teamMembers = [] }) => {
  if (!optionId) return "";
  if (optionId.startsWith("person:")) {
    const id = optionId.slice("person:".length);
    return persons.find((p) => p.id === id)?.email || "";
  }
  if (optionId.startsWith("team:")) {
    const id = optionId.slice("team:".length);
    return teamMembers.find((m) => m.employeeUuid === id)?.email || "";
  }
  if (optionId.startsWith("org:")) {
    const id = optionId.slice("org:".length);
    return partners.find((p) => p.id === id)?.email || "";
  }
  return "";
};

export const buildProtokollRecipients = (sitzung, sources) => {
  const emails = (sitzung.anwesende || [])
    .map((a) => resolveAttendeeEmail(a.optionId, sources))
    .filter(Boolean);
  return Array.from(new Set(emails));
};

// Interne Notizen (sichtbarExtern=false) dürfen ins externe Protokoll nicht
// gelangen. Diese Filterung ist die einzige Stelle, die über intern/extern
// entscheidet – alle Aufrufer (auch generateSitzungPdf.js, US-SITZ-005)
// müssen ihre Daten unverändert (ungefiltert) hier durchreichen, statt die
// Prüfung ein zweites Mal zu implementieren.
export const filterForMode = (pendenzen, modus) =>
  modus === "extern" ? pendenzen.filter((p) => p.sichtbarExtern === true) : pendenzen;

const PROTOKOLL_COLUMNS = [
  { header: "Nr", width: 6, get: (p) => p.nummer },
  { header: "Art", width: 12, get: (p) => ART_LABELS[p.typ] || p.typ },
  { header: "Beschreibung", width: 50, get: (p) => richTextToPlainText(p.beschreibung) },
  { header: "Themen", width: 20, get: (p) => (p.themen || []).join(", ") },
  {
    header: "Verantwortlich",
    width: 24,
    // Im externen Protokoll die Namen einzelner Team-Mitglieder NICHT preisgeben
    // (nur "Malheur&Fortuna"); intern die volle Darstellung.
    get: (p, modus) =>
      (modus === "extern" ? p.verantwortlichExtern : p.verantwortlich) || "",
  },
  { header: "Deadline", width: 13, get: (p) => formatDateDE(p.terminSoll) },
  { header: "neue Deadline", width: 13, get: (p) => formatDateDE(p.terminNeu) },
  { header: "Status", width: 13, get: (p) => STATUS_LABELS[p.status] || p.status },
];

const writeTable = (ws, startRow, title, pendenzen, modus = "intern") => {
  let row = startRow;
  ws.getCell(row, 1).value = title;
  ws.getCell(row, 1).font = { bold: true, size: 12 };
  row += 1;

  PROTOKOLL_COLUMNS.forEach((c, idx) => {
    const cell = ws.getCell(row, idx + 1);
    cell.value = c.header;
    cell.font = { bold: true, size: 9 };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
  });
  row += 1;

  pendenzen.forEach((p) => {
    PROTOKOLL_COLUMNS.forEach((c, idx) => {
      ws.getCell(row, idx + 1).value = c.get(p, modus);
    });
    ws.getCell(row, 3).alignment = { wrapText: true, vertical: "top" };
    row += 1;
  });

  return row + 1; // eine Leerzeile nach der Tabelle
};

// Baut das Protokoll-Workbook. modus: "intern" (alles) | "extern" (nur sichtbarExtern).
export const buildProtokollWorkbook = ({
  sitzung,
  project,
  pendenzenDieserSitzung,
  offenePendenzenFrueher = [],
  modus = "intern",
}) => {
  if (!sitzung) throw new Error("Keine Sitzung übergeben.");

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Malheur&Fortuna GmbH";
  workbook.lastModifiedBy = "Malheur&Fortuna GmbH";
  workbook.created = new Date();
  workbook.modified = new Date();

  const ws = workbook.addWorksheet(modus === "extern" ? "Protokoll (extern)" : "Protokoll");
  ws.columns = PROTOKOLL_COLUMNS.map((c) => ({ width: c.width }));

  let row = 1;
  ws.getCell(row, 1).value = "Sitzungsprotokoll";
  ws.getCell(row, 1).font = { bold: true, size: 14 };
  row += 2;

  ws.getCell(row, 1).value = `Projekt: ${project?.code || ""} – ${project?.title || ""}`;
  row += 1;
  ws.getCell(row, 1).value = `${sitzung.art} Nr. ${sitzung.nr} vom ${formatDateDE(sitzung.datum)}`;
  row += 1;
  if (sitzung.ort) {
    ws.getCell(row, 1).value = `Ort: ${sitzung.ort}`;
    row += 1;
  }
  row += 1;

  ws.getCell(row, 1).value = "Anwesende";
  ws.getCell(row, 1).font = { bold: true };
  row += 1;
  const anwesende = sitzung.anwesende || [];
  if (anwesende.length === 0) {
    ws.getCell(row, 1).value = "–";
    row += 1;
  } else {
    anwesende.forEach((a) => {
      ws.getCell(row, 1).value = attendeeDisplay(a);
      row += 1;
    });
  }
  row += 1;

  const gefilterteDieserSitzung = filterForMode(pendenzenDieserSitzung, modus);
  const gruppen = [
    { key: "P", label: "Pendenzen" },
    { key: "E", label: "Entscheide" },
    { key: "N", label: "Notizen" },
  ];
  gruppen.forEach((g) => {
    const items = gefilterteDieserSitzung.filter((p) => p.typ === g.key);
    if (items.length === 0) return;
    row = writeTable(ws, row, g.label, items, modus);
  });
  if (gefilterteDieserSitzung.length === 0) {
    ws.getCell(row, 1).value = "Keine Einträge zu dieser Sitzung.";
    row += 2;
  }

  const gefilterteOffene = filterForMode(offenePendenzenFrueher, modus);
  if (gefilterteOffene.length > 0) {
    row = writeTable(ws, row, "Offene Pendenzen aus früheren Sitzungen (Übertrag)", gefilterteOffene, modus);
  }

  return workbook;
};

export const protokollFilename = (project, sitzung) =>
  `Protokoll_${sanitizeFilenamePart(project?.code)}_${sanitizeFilenamePart(sitzung?.art)}_${
    sitzung?.datum || todayIso()
  }.xlsx`;

// Generiert das Protokoll und löst den Download aus.
export const downloadProtokoll = async (params) => {
  try {
    const workbook = buildProtokollWorkbook(params);
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = protokollFilename(params.project, params.sitzung);
    a.click();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Fehler beim Erzeugen des Protokolls:", error);
    throw new Error("Das Protokoll konnte nicht erzeugt werden.");
  }
};

// Bereitet eine E-Mail vor (mailto). Ein Datei-Anhang ist per mailto-Link nicht
// möglich, daher wird die Empfängerliste vorbefüllt und der Text bittet um
// manuellen Anhang der zuvor heruntergeladenen Datei.
export const buildProtokollMailto = ({ recipients = [], project, sitzung, dateiname }) => {
  const subject = `Protokoll ${sitzung.art} Nr. ${sitzung.nr} – ${project?.code || ""}`;
  const body = [
    `Anbei das Protokoll zur ${sitzung.art} Nr. ${sitzung.nr} vom ${formatDateDE(sitzung.datum)}.`,
    "",
    `Bitte die heruntergeladene Datei "${dateiname}" manuell anhängen.`,
  ].join("\n");
  const to = recipients.map(encodeURIComponent).join(",");
  const query = `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return `mailto:${to}?${query}`;
};
